import type { ProteinCalcProduct } from '@bulknutrients/bulk-shared-lib/interfaces/ProteinCalculator';
import { money } from '@bulknutrients/bulk-shared-lib/utils';
import type { ReactElement } from 'react';

export const DEFAULT_SERVE_SIZE = 30;
export const DEFAULT_SERVES_PER_DAY = 1;

/**
 * Round a number and remove any trailing zeros
 * @param {Number} num The Number to round
 * @param {Number} decimals Number of decimal points to round to
 * @return {Number}
 */
export const round = (num: number, decimals = 2): number =>
  Number(num.toFixed(decimals));

export function ProductProperties({
  product,
  comparisonProduct,
  purest,
}: {
  product: ProteinCalcProduct;
  comparisonProduct?: ProteinCalcProduct;
  purest: string;
}): ReactElement {
  function totalProtein(prod = product) {
    if (!prod.weight) {
      const weight = prod.displayWeight.includes('kg')
        ? Number(prod.displayWeight.split('kg')[0]) * 1000
        : Number.parseInt(prod.displayWeight.split('g')[0], 10);

      return Math.floor((weight * Number.parseInt(prod.protein, 10)) / 100);
    }
    return Math.floor(
      (Number.parseInt(prod.weight, 10) * Number.parseInt(prod.protein, 10)) /
        100
    );
  }
  function pricePerServe(prod = product) {
    return round(
      (DEFAULT_SERVE_SIZE / totalProtein(prod)) *
        round((prod.price as number) / 100),
      2
    );
  }
  function pricePerMonth(prod = product) {
    return round(pricePerServe(prod) * DEFAULT_SERVES_PER_DAY * 30);
  }
  function isOwnProduct(prod = product) {
    return prod.brand === 'Bulk Nutrients';
  }
  function isCheapest({ cheapest }: { cheapest: number }) {
    return isOwnProduct() && cheapest === pricePerServe();
  }
  function isPurest(prod = product) {
    return isOwnProduct() && purest === prod.protein;
  }

  function savingsPerMonth() {
    if (!comparisonProduct) return null;
    const savings = pricePerMonth(comparisonProduct) - pricePerMonth();
    return savings > 0 ? savings : null;
  }

  return (
    <div
      className="border-0 border-b border-t border-dashed border-grey-mid text-center
    dark:border-white"
    >
      <div className="relative border-0 border-b border-dashed border-grey-mid p-5 dark:border-white">
        {isPurest() && (
          <div className="absolute bottom-1 left-1/2 hidden -translate-x-1/2 whitespace-nowrap font-bold uppercase tracking-wider text-green">
            Purest!
          </div>
        )}
        <div className="relative m-0 text-4xl font-secondary dark:text-grey-light">
          {round(Number.parseInt(product.protein, 10))}%
        </div>
        <div className="mb-1 block text-black-mid dark:text-grey-mid">
          Percent protein
        </div>
      </div>
      <div className="relative border-0 border-b border-dashed border-grey-mid p-5 dark:border-white">
        <div className="relative m-0 text-4xl font-secondary dark:text-grey-light">
          {totalProtein().toLocaleString()} grams
        </div>
        <div className="mb-1 block text-black-mid dark:text-grey-mid">
          Of actual protein per pack
        </div>
      </div>
      <div className="relative border-0 border-b border-dashed border-grey-mid p-5 dark:border-white">
        {isCheapest({ cheapest: 2 }) && (
          <div className="absolute bottom-1 left-1/2 hidden -translate-x-1/2 whitespace-nowrap font-bold uppercase tracking-wider text-green">
            Best Value!
          </div>
        )}
        <div className="relative m-0 text-4xl font-secondary dark:text-grey-light">
          {money(pricePerServe(product))}
        </div>
        <div className="mb-1 block text-black-mid dark:text-grey-mid">
          Per {DEFAULT_SERVE_SIZE}g serve of actual protein
        </div>
      </div>
      <div className="relative p-5">
        <div className="relative m-0 text-4xl font-secondary dark:text-grey-light">
          {money(pricePerMonth(product))}
        </div>
        <div className="mb-1 block text-black-mid dark:text-grey-mid">
          How much you'd pay per month
        </div>
        {savingsPerMonth() && (
          <div
            className="absolute bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap 
          font-bold uppercase tracking-wider text-green"
          >
            You'd save
            <span className="ProductProperties__SavingsValue">
              {money(savingsPerMonth())}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
